/**
 * The only outward link the product has: a thread, open in Gmail, in a new
 * tab. It is a read view of mail the person already has — nothing is sent,
 * drafted or changed, and no code path in `apps/web` reaches a Gmail endpoint
 * that is not a read (the static check is `gates/no-outbound-path.test.ts`,
 * M2.11/COR-57, which walks `apps/` and `packages/` for send and modify
 * scopes and fails CI on any).
 *
 * `u/0` assumes the first signed-in account (docs/section-actions.md ▲3).
 * ▲3 also names an `rfc822msgid:` fallback for a non-Gmail mailbox; a
 * `SourceRef` carries `provider_message_id` (the Gmail id) and not the RFC822
 * `Message-ID`, so that fallback is unreachable from the ledger today and the
 * action is unavailable instead, saying so.
 */
import type { Ledger, LedgerItem } from '@cordelia/core'

export const THREAD_BASE = 'https://mail.google.com/mail/u/0/#all/'

export const threadLink = (provider_thread_id: string | null): string | null =>
  provider_thread_id === null || provider_thread_id.length === 0
    ? null
    : `${THREAD_BASE}${provider_thread_id}`

/** Why the deep link is unavailable, when it is. Shown beside the verb. */
export const NO_THREAD = 'The message this came from carries no thread to open.'

/**
 * The thread behind a card: the source of the first evidence on its head
 * claim — the message the card quotes, so the verb opens what the card shows.
 */
export function threadLinkFor(ledger: Ledger, item: LedgerItem): string | null {
  const claim = ledger.claims.find((c) => c.id === item.claim_id)
  const first = claim?.evidence_ids[0]
  if (first === undefined) return null
  const evidence = ledger.evidence.find((e) => e.id === first)
  if (evidence === undefined) return null
  const ref = ledger.sources.find((s) => s.source_id === evidence.source_id)
  return threadLink(ref?.provider_thread_id ?? null)
}
